import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { Button, Container, SectionLabel, SectionTitle } from '@/components/primitives';
import { queryFn } from '@/api';
import { navigated, reportSelected } from '@/model/router';

type HistoryItem = {
  id: string;
  searchId: string;
  generatedAt: string;
  signals: number;
  scoreValue: number;
  title: string;
};

const Page = styled.section`
  padding: 80px 0 120px;
`;

const List = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.button`
  text-align: left;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px 96px 88px;
  gap: 24px;
  align-items: center;
  padding: 20px 24px;
  border: 1px solid var(--surface-border, rgba(255, 255, 255, 0.08));
  background: var(--surface-card, rgba(255, 255, 255, 0.03));
  border-radius: 16px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: background 0.15s;
  &:hover {
    background: var(--surface-card-hover, rgba(255, 255, 255, 0.06));
  }
  & .title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 0.85;
  }
  & .meta {
    opacity: 0.6;
    font-variant-numeric: tabular-nums;
  }
  & .score {
    font-weight: 500;
    text-align: right;
  }
  & .date {
    opacity: 0.45;
    font-size: 0.86em;
    text-align: right;
  }
`;

const Empty = styled.p`
  opacity: 0.5;
  margin-top: 32px;
`;

const Foot = styled.div`
  margin-top: 48px;
  display: flex;
  justify-content: space-between;
`;

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const ReportHistory = () => {
  const history = useQuery({
    queryKey: ['/api/lume/report/history'],
    queryFn: queryFn<HistoryItem[]>('/api/lume/report/history'),
    staleTime: 60_000,
  });

  return (
    <Page>
      <Container>
        <SectionLabel>история</SectionLabel>
        <SectionTitle>прошлые отчёты</SectionTitle>

        {history.isLoading && <Empty>загрузка…</Empty>}
        {!history.isLoading && (history.data ?? []).length === 0 && (
          <Empty>пока нет завершённых отчётов</Empty>
        )}

        <List>
          {(history.data ?? []).map((it) => (
            <Row key={it.id} onClick={() => reportSelected(it.id)}>
              <span className="title">{it.title}</span>
              <span className="meta">{it.signals} сигн.</span>
              <span className="score">{it.scoreValue}/100</span>
              <span className="date">{formatDate(it.generatedAt)}</span>
            </Row>
          ))}
        </List>

        <Foot>
          <Button $variant="ghost" onClick={() => navigated('landing')}>
            ← На главную
          </Button>
          <Button $variant="ghost" onClick={() => reportSelected(null)}>
            Открыть последний →
          </Button>
        </Foot>
      </Container>
    </Page>
  );
};
